import React, { useRef, useEffect } from "react";
import {
  useTable,
  useFilters,
  usePagination,
  useSortBy,
  useGroupBy,
  useExpanded,
} from "react-table";
import styled from "styled-components";
import { useSticky } from "react-table-sticky";
import cx from "classnames";
import { SiPivotaltracker } from "react-icons/si";

import IconSvgComponent from "components/common/IconSvgComponent";
import { UnstyledButton } from "styles/components/Btns";
import Loader from "components/common/loader";
import { IoMdArrowDropright } from "react-icons/io";
import { Virtuoso } from "react-virtuoso";

const Container = styled.div`
  .table {
    &.sticky {
      width: 100%;
      overflow-x: scroll;

      .header,
      .body {
        position: sticky;
        z-index: 1;
        width: fit-content;
      }

      .header {
        top: 0;
      }

      .body {
        position: relative;
        z-index: 0;
      }

      [data-sticky-td] {
        position: sticky;
        background-color: #ffffff;
        border: 1px solid #ffffff;
      }

      [data-sticky-last-left-td] {
        border-right: 1px solid #9797971a;
      }

      [data-sticky-first-right-td] {
        border-left: 1px solid #9797971a;
      }
    }
  }
`;

const StyledTable = styled.div``;

const StyledTableHeader = styled.div`
  border-bottom: 1px solid #9797971a;
`;

const StyledTableHeadCell = styled.div`
  padding: 12px 16px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  text-align: left;
  white-space: nowrap;
  overflow-x: clip;
  text-overflow: ellipsis;
  user-select: none;
  border-bottom: 2px solid transparent;
  border-top: 2px solid transparent;

  ${(props) =>
    props.isSorted &&
    !props.isSortedDesc &&
    `
      border-top: 2px solid #96a0b2;
    `}

  ${(props) =>
    props.isSorted &&
    props.isSortedDesc &&
    `
      border-bottom: 2px solid #96a0b2;
  `}

  ${(props) =>
    props.isGrouped &&
    `
      // background-color: #96a0b2;
  `}

  ${(props) =>
    props.width &&
    `
    flex: 1 1 ${props.width}px;
    width: ${props.width}px;
  `}

  ${(props) =>
    props.minWidth &&
    `
    flex: 1 1 ${props.minWidth}px;
    min-width: ${props.minWidth}px;
  `}

  ${(props) =>
    props.maxWidth &&
    `
    max-width: ${props.maxWidth}px;
  `}
`;

const StyledTableBodyContainer = styled.div``;

const StyledTableRow = styled.div`
  min-height: 30px;
  min-width: fit-content;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #9797971a;
  transition: background-color 0.5s ease;

  ${(props) =>
    props.isSelected &&
    `
      background-color: #88c7f6;
  `}

  ${(props) =>
    props.isGrouped &&
    `
      border-bottom: 1px solid #88c7f6;
  `}
`;

const StyledTableCell = styled.div`
  padding: 12px 16px;
  text-align: left;
  white-space: nowrap;
  overflow-x: clip;
  text-overflow: ellipsis;

  ${(props) =>
    props.width &&
    `
    flex: 1 1 ${props.width}px;
    width: ${props.width}px;
  `}

  ${(props) =>
    props.minWidth &&
    `
    flex: 1 1 ${props.minWidth}px;
    min-width: ${props.minWidth}px;
  `}

  ${(props) =>
    props.maxWidth &&
    `
    max-width: ${props.maxWidth}px;
    width: ${props.minWidth}px;
  `}
  ${(props) => {
    // console.log({ props });
    return ``;
  }}
`;

const StyledTableBody = styled.div`
  width: 100%;
  position: relative;
`;

const ArrowIcon = ({ isOpen, className }) => {
  const baseClass = "arrow";
  const classes = cx(
    baseClass,
    { [`${baseClass}--closed`]: !isOpen },
    { [`${baseClass}--open`]: isOpen },
    className
  );
  return (
    <IoMdArrowDropright className={classes} style={{ display: "flex", alignItems: "center" }} />
  );
};

const Table = (props) => {
  const {
    data,
    fetchData,
    columns,
    isLoading = true,
    isFilteringEnabled = false,
    serverSidePagination = false,
    isSortingEnabled = !serverSidePagination,
    count = 100,
    defaultGroupBy = [],
    NoDataComponent,
    LoadingComponent = () => {
      return (
        <div style={{ height: "487px" }}>
          <Loader />
        </div>
      );
    },
    renderRowSubComponent,
    showPagination = true,
    isSticky = false,
    // SubComponent,
    // defaultFilterMethod = () => {},
    // defaultSorted = [],
    // getTrProps = () => {},
    // getTdProps = () => {},
    meta = {},
    classNames = "",
    style,
    enableGrouping = false,
  } = props;

  const parentRef = useRef();
  const tableBodyRef = useRef();

  const instance = useTable(
    {
      columns,
      data,
      manualFilters: serverSidePagination, // true == server side filtering
      disableFilters: !isFilteringEnabled,
      manualSortBy: serverSidePagination, // true == server side sorting
      disableSortBy: !isSortingEnabled,
      disableGroupBy: !enableGrouping,
      initialState: {
        groupBy: defaultGroupBy,
      },
      meta: {
        ...meta,
        tableBodyRef,
      },
    },
    useFilters,
    useSticky,
    useGroupBy,
    useSortBy,
    useExpanded
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,

    prepareRow,
    state: { filters, groupBy, expanded },
  } = instance;

  return (
    <Container isSticky={isSticky} style={style} className={classNames}>
      <StyledTable {...getTableProps()} className={isSticky ? "table sticky" : ""}>
        <StyledTableHeader className="header">
          {headerGroups.map((headerGroup, index) => {
            return isFilteringEnabled ? (
              <div {...headerGroup.getHeaderGroupProps()} className="tr">
                <StyledTableRow>
                  {headerGroup.headers.map((column) => {
                    return (
                      <StyledTableHeadCell
                        className="th"
                        title={column.render("Header")}
                        isSorted={column.isSorted}
                        isSortedDesc={column.isSortedDesc}
                        {...column.getHeaderProps(
                          isSticky
                            ? {
                                width: column.minWidth,
                                maxWidth: column.maxWidth,
                                ...column.getSortByToggleProps(),
                              }
                            : {
                                minWidth: column.minWidth,
                                maxWidth: column.maxWidth,
                                ...column.getSortByToggleProps(),
                              }
                        )}
                      >
                        {column.canGroupBy ? (
                          // If the column can be grouped, let's add a toggle
                          <span {...column.getGroupByToggleProps()}>
                            {column.isGrouped ? "🛑 " : "👊 "}
                          </span>
                        ) : null}
                        {column.render("Header")}
                      </StyledTableHeadCell>
                    );
                  })}
                </StyledTableRow>
                <StyledTableRow {...headerGroup.getHeaderGroupProps({ key: `${index}-filter` })}>
                  {headerGroup.headers.map((column) => {
                    return (
                      <StyledTableHeadCell
                        {...column.getHeaderProps(
                          isSticky
                            ? {
                                width: column.width,
                                style: column.style,
                              }
                            : {
                                minWidth: column.minWidth,
                                maxWidth: column.maxWidth,
                              }
                        )}
                      >
                        {column.canFilter && column.render("Filter")}
                      </StyledTableHeadCell>
                    );
                  })}
                </StyledTableRow>
              </div>
            ) : (
              <StyledTableRow {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => {
                  return (
                    <StyledTableHeadCell
                      className="th"
                      title={column.render("Header")}
                      isSorted={column.isSorted}
                      isSortedDesc={column.isSortedDesc}
                      isGrouped={column.isGrouped}
                      {...column.getHeaderProps(
                        isSticky
                          ? {
                              width: column.width,
                              style: column.style,
                              ...column.getSortByToggleProps(),
                            }
                          : {
                              minWidth: column.minWidth,
                              maxWidth: column.maxWidth,
                              ...column.getSortByToggleProps(),
                            }
                      )}
                    >
                      {column.canGroupBy ? (
                        // If the column can be grouped, let's add a toggle
                        <span
                          {...column.getGroupByToggleProps({
                            style: {
                              marginRight: "4px",
                              marginBottom: "-2px",
                            },
                            onClick: (event) => {
                              event.stopPropagation();
                              column.getGroupByToggleProps().onClick(event);
                            },
                          })}
                        >
                          {column.isGrouped ? (
                            <SiPivotaltracker
                              style={{
                                height: "14px",
                                width: "14px",
                                color: "#484848",
                                marginBottom: "-3px",
                              }}
                            />
                          ) : (
                            <SiPivotaltracker
                              style={{
                                height: "14px",
                                width: "14px",
                                color: "#888888",
                                marginBottom: "-3px",
                              }}
                            />
                          )}
                        </span>
                      ) : null}
                      {column.render("Header")}
                    </StyledTableHeadCell>
                  );
                })}
              </StyledTableRow>
            );
          })}
        </StyledTableHeader>

        <StyledTableBodyContainer innerRef={parentRef}>
          <StyledTableBody
            {...getTableBodyProps()}
            id="tableBody"
            className="body"
            innerRef={tableBodyRef}
          >
            {isLoading && <LoadingComponent />}

            {!isLoading && (
              <Virtuoso
                // skipAnimationFrameInResizeObserver={true}
                style={{ height: "70vh" }}
                // defaultItemHeight={46}
                // fixedItemHeight={46}
                data={rows}
                components={{
                  EmptyPlaceholder: () => {
                    return (
                      <div
                        style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        <NoDataComponent />
                      </div>
                    );
                  },
                }}
                itemContent={(index, row) => {
                  prepareRow(row);
                  return (
                    <>
                      <StyledTableRow
                        {...row.getRowProps({
                          isSelected: row.values.isSelected,
                        })}
                        className="tr"
                        isGrouped={row.isGrouped}
                      >
                        {row.cells.map((cell) => {
                          // console.log({ cell });
                          return (
                            <StyledTableCell
                              className="td"
                              title={cell.value?.toString() || ""}
                              {...cell.getCellProps(
                                isSticky
                                  ? {
                                      width: cell.column.width,
                                      style: cell.column.style,
                                    }
                                  : {
                                      minWidth: cell.column.minWidth,
                                      maxWidth: cell.column.maxWidth,
                                      style: cell.column.style,
                                    }
                              )}
                            >
                              {cell.isGrouped ? (
                                // If it's a grouped cell, add an expander and row count
                                <span
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <span
                                    {...row.getToggleRowExpandedProps({
                                      style: { marginBottom: "1px" },
                                    })}
                                  >
                                    <ArrowIcon isOpen={row.isExpanded} />
                                  </span>{" "}
                                  {cell.render("Cell", {
                                    tableBodyRef,
                                  })}{" "}
                                  ({row.subRows.length})
                                </span>
                              ) : cell.isAggregated ? (
                                // If the cell is aggregated, use the Aggregated
                                // renderer for cell
                                cell.render("Aggregated", {
                                  tableBodyRef,
                                })
                              ) : cell.isPlaceholder ? null : ( // For cells with repeated values, render null
                                // Otherwise, just render the regular cell
                                cell.render("Cell", {
                                  tableBodyRef,
                                })
                              )}
                            </StyledTableCell>
                          );
                        })}
                      </StyledTableRow>
                    </>
                  );
                }}
              />
            )}
          </StyledTableBody>
        </StyledTableBodyContainer>
      </StyledTable>
    </Container>
  );
};

export default Table;
